/**
 * Shared helpers for the "News" video category.
 *
 * News videos live in the same `video_library` table as everything else but
 * are tagged with the `news` category. They appear ONLY on /news (a square
 * 1080×1080 player) and are filtered out of the main /videos catalog, the
 * landing free-videos slide, and the student library.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const NEWS_CATEGORY_SLUG = "news";

/** Resolve the News category id (cached for an hour). */
export function useNewsCategoryId() {
  const { data } = useQuery({
    queryKey: ["news-category-id"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("video_categories")
        .select("id")
        .eq("slug", NEWS_CATEGORY_SLUG)
        .maybeSingle();
      if (error) throw error;
      return (data?.id as string | undefined) ?? null;
    },
    staleTime: 60 * 60 * 1000,
  });
  return data ?? null;
}

/** Generate a stable, dated slug for a News video. */
export function generateNewsSlug(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `news-from-${y}-${m}-${d}`;
}
